// Client entry for the Cattle Ticker homepage (rb-home-ticker.html).
//
// Hydrates rather than renders: the HTML already contains the full page from
// the build-time prerender — including the day's real auction prices — and
// createRoot would throw that away, which is the entire reason this entry
// exists.
//
// Deliberately NOT main.tsx, and deliberately not sharing entry-rbnext.tsx:
// this document is one static page with no router, no React Query and no
// Supabase client. Pulling App.tsx in would hand the front door of the site
// the whole application bundle.

import { hydrateRoot } from "react-dom/client";
import { startWebAnalytics } from "./lib/webAnalytics";

import "@fontsource-variable/geist";
import "@fontsource-variable/geist-mono";
import "./index.css";
import "./brand";

import {
  EMPTY_RAIL,
  EMPTY_TICKER,
  type RailPayload,
  type TickerPayload,
} from "./lib/rbCattleTicker";
import type { RbIndexTile } from "./lib/rbIndexes";
import type { FeederFlashEpisode } from "./lib/rbFeederFlash";
import type { HomeKpi } from "./lib/rbHomeKpis";
import { RB_TOOLS_FALLBACK, type RbToolsPayload } from "./lib/rbTools";
import RbTickerHome from "./pages/rbnext/RbTickerHome";
import { attachTickerAuth } from "./lib/tickerAuth";

declare global {
  interface Window {
    /** The tape, baked in by the prerender. Read on the first client render so
     *  the hydrated tree matches the served HTML exactly; TickerBoard then
     *  refreshes it in an effect, and again every minute.
     *
     *  Its own slot rather than sharing the other homepages' — the three pages
     *  carry different shapes, and reusing a name would make a mismatch look
     *  like missing data. */
    __RB_TICKER__?: TickerPayload;
    /** The PACKER SIDE rail's three figures, baked in by the prerender for the
     *  same reason as the tape: the first client render must reproduce the
     *  served HTML, and three cards filling in afterwards would reflow the
     *  rail a beat after paint. */
    __RB_RAIL__?: RailPayload;
    /** The latest Feeder Flash episode, baked by the prerender. Explicitly
     *  nullable: no episode is a normal state and renders no tile. */
    __RB_FEEDER_FLASH__?: FeederFlashEpisode | null;
    __RB_INDEXES__?: RbIndexTile[];
  }
}

// ⚠️ A MEMBER'S SESSION REACHES THE INDEX READERS ONLY THROUGH THIS (Premium
// lock, 2026-10-13). This entry is not main.tsx, so nothing else hands the
// ticker library the token, and a paying member would be read as a stranger.
// The Supabase client loads by dynamic import inside, never at module scope —
// see tickerAuth.ts — so the prerender is untouched.
void attachTickerAuth();

const root = document.getElementById("root");
if (root) {
  hydrateRoot(
    root,
    <RbTickerHome
      initialKpis={window.__RB_HOME_KPIS__ ?? []}
      initialTools={window.__RB_TOOLS__ ?? RB_TOOLS_FALLBACK}
      // Falls back to the empty board rather than undefined: an absent tape
      // must render as "no sales posted", never as a crash on the front door.
      initialTicker={window.__RB_TICKER__ ?? EMPTY_TICKER}
      initialRail={window.__RB_RAIL__ ?? EMPTY_RAIL}
      initialFeederFlash={window.__RB_FEEDER_FLASH__ ?? null}
      initialRbIndexes={window.__RB_INDEXES__ ?? []}
    />,
  );
}

// `__RB_HOME_KPIS__` and `__RB_TOOLS__` are declared by entry-rbnext.tsx, which
// this build also compiles — the global interface merges across both files.
export type { HomeKpi };

// Pageviews, after the page is up (src/lib/webAnalytics.ts).
startWebAnalytics();
